import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/accessibility";
import "./highcharts-responsive.css";
import { CardContent } from "@/components/ui/card";
import { useTenancy } from "@/contexts/TenancyContext";
import { Skeleton } from "@/components/ui/skeleton";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import type { CorrectiveAction } from "@/app/pages/auditcentre";

// Colors for the chart
const CHART_COLORS = {
  open: "#F66A6A",
  closed: "#4CAF50",
  empty: "#D3D3D3"
};

const getChartOptions = (openCount: number, closedCount: number, total: number): Highcharts.Options => ({
  chart: {
    type: "pie",
    backgroundColor: "transparent",
    spacing: [0, 0, 0, 0],
    height: '100%',
  },
  title: {
    text: undefined,
  },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)'
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  plotOptions: {
    pie: {
      innerSize: "65%",
      borderWidth: 0,
      dataLabels: { 
        enabled: false 
      },
      size: "100%",
      showInLegend: true
    },
  },
  legend: {
    enabled: false
  },
  series: [
    {
      type: "pie",
      name: "Corrective Actions",
      data: total === 0 ? [
        { name: "No Data", y: 1, color: CHART_COLORS.empty }
      ] : [
        { 
          name: "Open", 
          y: openCount, 
          color: CHART_COLORS.open 
        },
        { 
          name: "Closed", 
          y: closedCount, 
          color: CHART_COLORS.closed 
        }
      ],
    },
  ],
});

const CorrectiveActionsSummaryWidget: React.FC = () => {
  // Refs
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Get tenancy context
  const { activeTenancy } = useTenancy();

  // Report widget status to dashboard context
  useWidgetStatusReporter('corrective-actions-summary', isLoading, error, total > 0);
  
  // Fetch corrective actions
  useEffect(() => {
    const fetchCorrectiveActions = async () => {
      if (!activeTenancy?.id || activeTenancy.id === "Loading...") {
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const db = getFirestore();
        const correctiveActionsRef = collection(db, `customers/${activeTenancy.id}/correctiveActions`);
        const snapshot = await getDocs(correctiveActionsRef);
        
        let open = 0;
        let closed = 0;
        
        snapshot.docs.forEach(doc => {
          const data = doc.data() as CorrectiveAction;
          if (data.status === "Open") {
            open++;
          } else if (data.status === "Closed") {
            closed++;
          }
        });
        
        setOpenCount(open);
        setClosedCount(closed);
        setTotal(open + closed);
      } catch (err) {
        console.error('[CorrectiveActionsSummaryWidget] Error fetching corrective actions:', err);
        setError('Failed to load corrective actions');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCorrectiveActions();
  }, [activeTenancy?.id]);
  
  // Handle resize events
  useEffect(() => {
    if (!containerRef.current) return;
    
    const handleResize = () => {
      if (chartRef.current?.chart?.reflow) {
        chartRef.current.chart.reflow();
        // Small delay to ensure proper rendering
        const timer = setTimeout(() => {
          chartRef.current?.chart?.reflow();
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    
    // Set initial size
    handleResize();
    
    // Set up resize observer
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);
    
    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  
  const chartOptions = getChartOptions(openCount, closedCount, total);
  
  // Render loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full p-4 flex flex-col items-center justify-center">
        <h3 className="text-lg font-semibold mb-4">Corrective Actions</h3>
        <Skeleton className="h-24 w-24 rounded-full mb-4" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardContent>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <CardContent className="h-full w-full p-4 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-medium">Error loading corrective actions</p>
          <p className="text-sm">{error}</p>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full p-0 flex flex-col items-center justify-center">
      <div className="w-full flex justify-between items-center p-2">
        <h3 className="text-lg font-semibold">Corrective Actions</h3>
        <span className="text-sm font-medium text-muted-foreground">
          Total: {total}
        </span>
      </div>
      <div
        ref={containerRef}
        className="h-full w-full flex items-center justify-center p-0"
        style={{ minHeight: 0, minWidth: 0 }}
      >
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
          containerProps={{ style: { width: '100%', height: '100%' } }}
        />
      </div>
      <div className="w-full flex justify-center gap-4 p-2 text-sm">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: CHART_COLORS.open }}></span>
          Open: {openCount}
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: CHART_COLORS.closed }}></span>
          Closed: {closedCount}
        </div>
      </div>
    </CardContent>
  );
};

export default CorrectiveActionsSummaryWidget;
